import { Dispatch, isValidElement, SetStateAction, useEffect, useState } from 'react';
import { PaginationRequest } from '../../clients/quipli-service/pagination/pagination';
import { DataTableColumn, DataTableProps, DataTableRow } from './data-table';
import { Spinner } from '../spinner/spinner';
import { Typography } from '../typography/typography';
import classNames from 'classnames';
import { Paginator } from '../paginator/paginator-v2';
import Icon from '../icons/icon';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { DragHandle } from './drag-handle';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export const MobileDataTable = <TPaginator extends PaginationRequest>({
  data,
  dataItems = [],
  setNewDataItemsOrder,
  tabs,
  columnNames,
  noDataText = 'No data available',
  isLoading,
  pagination,
  request,
  setRequest,
  cypressPrevious,
  cypressNext,
}: DataTableProps<TPaginator>) => {
  const [openMobileRow, setOpenMobileRow] = useState<number | null>(null);
  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));
  const [activeId, setActiveId] = useState<number | undefined>();

  useEffect(() => {
    setOpenMobileRow(null);
  }, [pagination, data]);

  function handleDragEnd(event: { active: any; over: any }) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = dataItems.findIndex((elem) => elem.id === active.id);
      const newIndex = dataItems.findIndex((elem) => elem.id === over.id);
      setNewDataItemsOrder(arrayMove(dataItems, oldIndex, newIndex));
    }
    setActiveId(undefined);
  }

  function handleDragStart(event: any) {
    setOpenMobileRow(null);
    setActiveId(event.active.id);
  }

  const selectedRow = (() => {
    if (activeId !== undefined && activeId >= 0) {
      const activeIndex = dataItems.findIndex((elem) => elem.id === activeId);
      return { row: data[activeIndex], idx: activeIndex };
    } else {
      return null;
    }
  })();

  return (
    <div className="bg-white">
      {tabs && tabs}
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext items={dataItems} strategy={verticalListSortingStrategy}>
          {data.length === 0 || isLoading ? (
            isLoading ? (
              <Spinner />
            ) : (
              <Typography type="text-sm" className="text-grey-500 flex items-center justify-center min-h-[100px]">
                {noDataText}
              </Typography>
            )
          ) : (
            data.map((elem: DataTableRow, idx: number) => {
              return (
                <MobileDraggableRow
                  columnNames={columnNames}
                  elem={elem}
                  idx={idx}
                  id={dataItems[idx].id}
                  key={idx}
                  openMobileRow={openMobileRow}
                  setOpenMobileRow={setOpenMobileRow}
                />
              );
            })
          )}
        </SortableContext>
        <DragOverlay>
          {activeId && <MobileStaticRow elem={selectedRow && selectedRow.row} idx={selectedRow?.idx} />}
        </DragOverlay>
      </DndContext>
      {setRequest && pagination && (
        <Paginator
          request={request}
          setRequest={(paginator) => setRequest((prev) => ({ ...prev, ...paginator }))}
          pagination={pagination}
          cypressPrevious={cypressPrevious}
          cypressNext={cypressNext}
        />
      )}
    </div>
  );
};
const MobileDraggableRow = ({
  columnNames,
  elem,
  idx,
  id,
  openMobileRow,
  setOpenMobileRow,
}: {
  columnNames: DataTableColumn[];
  elem: DataTableRow;
  idx: number;
  id: number;
  openMobileRow: number | null;
  setOpenMobileRow: Dispatch<SetStateAction<number | null>>;
}) => {
  const { attributes, listeners, transform, transition, setNodeRef } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };
  return (
    <div key={idx} className="mobile-datatable-borders" ref={setNodeRef} style={style}>
      <div id={`mobile-table-row-${idx}`} key={idx} className="bg-white p-[16px] flex justify-between items-center">
        <div className="flex gap-4">
          <DragHandle attributes={attributes} listeners={listeners} />
          {elem.mobileHeader}
        </div>
        {elem.mobileHeaderAction ? (
          <div>{elem.mobileHeaderAction}</div>
        ) : (
          <div
            onClick={() => {
              document.getElementById(`mobile-table-row-${idx}`)?.scrollIntoView();
              setOpenMobileRow((prevValue: number | null) => (prevValue === idx ? null : idx));
            }}
          >
            <Icon icon="ChevronDown" />
          </div>
        )}
      </div>
      <div
        key={`content-${idx}`}
        className={classNames('hidden bg-white p-[16px] flex-wrap collapse-transition', {
          '!flex': openMobileRow === idx,
        })}
      >
        {columnNames.map(
          (
            {
              label,
              key,
              isCheckBoxColumn,
              showRule = 'Both',
              mobileShowRule = 'fullrow',
              hide = false,
            }: DataTableColumn,
            idx: number,
          ) => {
            return (
              !isCheckBoxColumn &&
              !hide &&
              showRule !== 'OnlyDesktop' && (
                <div
                  key={idx}
                  className={classNames('w-full min-h-[52px] flex', {
                    'mobile-datatable-borders': columnNames.length !== idx + 1 && mobileShowRule !== 'halfrow',
                    'flex-[0_1_50%] my-[5px]': mobileShowRule === 'halfrow',
                  })}
                >
                  {mobileShowRule === 'fullrow' ? (
                    <>
                      {label && (
                        <div className="flex-[0_1_50%] flex items-center">
                          <Typography type="text-md" className="text-grey-500">
                            {label !== undefined ? label : key}
                          </Typography>
                        </div>
                      )}

                      <div
                        className={classNames('flex items-center', {
                          'flex-[0_1_50%]': label,
                          'flex-[0_1_100%] justify-center': !label,
                        })}
                      >
                        {isValidElement(elem.fields[key]) ? (
                          elem.fields[key]
                        ) : (
                          <Typography type="text-sm" className="text-quipli-black-800">
                            {elem.fields[key]}
                          </Typography>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {
                        <div className="flex-[0_1_50%] flex flex-col justify-start">
                          <Typography type="text-md" className="text-grey-500">
                            {label !== undefined ? label : key}
                          </Typography>
                          {isValidElement(elem.fields[key]) ? (
                            elem.fields[key]
                          ) : (
                            <Typography type="text-sm" className="text-quipli-black-800">
                              {elem.fields[key]}
                            </Typography>
                          )}
                        </div>
                      }
                    </>
                  )}
                </div>
              )
            );
          },
        )}
      </div>
    </div>
  );
};
const MobileStaticRow = ({ elem, idx }: { elem: DataTableRow | null; idx: number | undefined }) => {
  return (
    <div key={idx} className="mobile-datatable-borders">
      <div id={`mobile-table-row-${idx}`} key={idx} className="bg-white p-[16px] flex justify-between items-center">
        <div className="flex gap-4">
          <DragHandle isDragging />
          {elem && elem.mobileHeader}
        </div>
        {elem && elem.mobileHeaderAction ? (
          <div>{elem.mobileHeaderAction}</div>
        ) : (
          <div>
            <Icon icon="ChevronDown" />
          </div>
        )}
      </div>
    </div>
  );
};
